import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useEffect, useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
} from "@volca/ui/components/ui/input-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { TooltipShortcut } from "@volca/ui/components/ui/tooltip-shortcut";
import { cn } from "@volca/ui/lib/utils";
import { ToolbarSegment } from "./toolbar-segment";

export interface SheetSearchControlProps {
  isOpen: boolean;
  focusRequestVersion: number;
  query: string;
  matchCount: number;
  activeMatchIndex: number;
  onOpen: () => void;
  onClose: () => void;
  onQueryChange: (query: string) => void;
  onPreviousMatch: () => void;
  onNextMatch: () => void;
}

export const SheetSearchControl = ({
  isOpen,
  focusRequestVersion,
  query,
  matchCount,
  activeMatchIndex,
  onOpen,
  onClose,
  onQueryChange,
  onPreviousMatch,
  onNextMatch,
}: SheetSearchControlProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasQuery = query.trim().length > 0;
  const hasMatches = matchCount > 0;

  useEffect(() => {
    if (!isOpen) return;

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [focusRequestVersion, isOpen]);

  if (!isOpen) {
    return (
      <ToolbarSegment>
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Search current sheet"
                  onClick={onOpen}
                />
              }
            >
              <MagnifyingGlassIcon />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <TooltipShortcut keys={["mod", "F"]}>
                Search current sheet
              </TooltipShortcut>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </ToolbarSegment>
    );
  }

  return (
    <ToolbarSegment className="px-2">
      <TooltipProvider delay={200}>
        <InputGroup className="w-[min(18rem,28vw)] min-w-40">
          <InputGroupAddon>
            <MagnifyingGlassIcon />
          </InputGroupAddon>
          <InputGroupInput
            ref={inputRef}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
              }

              if (event.key === "Enter" && hasMatches) {
                event.preventDefault();
                if (event.shiftKey) {
                  onPreviousMatch();
                } else {
                  onNextMatch();
                }
              }
            }}
            type="text"
            aria-label="Search current sheet"
            placeholder="Find in sheet"
          />
          <InputGroupAddon align="inline-end">
            {hasQuery && (
              <InputGroupText
                className={cn("text-xs whitespace-nowrap", {
                  "text-danger": !hasMatches,
                })}
              >
                {hasMatches
                  ? `${activeMatchIndex + 1} of ${matchCount}`
                  : "No matches"}
              </InputGroupText>
            )}
            <Tooltip>
              <TooltipTrigger
                render={
                  <InputGroupButton
                    size="icon-xs"
                    aria-label="Previous search result"
                    disabled={!hasMatches}
                    onClick={onPreviousMatch}
                  />
                }
              >
                <CaretUpIcon />
              </TooltipTrigger>
              <TooltipContent side="bottom">Previous result</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <InputGroupButton
                    size="icon-xs"
                    aria-label="Next search result"
                    disabled={!hasMatches}
                    onClick={onNextMatch}
                  />
                }
              >
                <CaretDownIcon />
              </TooltipTrigger>
              <TooltipContent side="bottom">Next result</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <InputGroupButton
                    size="icon-xs"
                    aria-label="Close sheet search"
                    onClick={onClose}
                  />
                }
              >
                <XIcon />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <TooltipShortcut keys={["Esc"]}>Close</TooltipShortcut>
              </TooltipContent>
            </Tooltip>
          </InputGroupAddon>
        </InputGroup>
      </TooltipProvider>
    </ToolbarSegment>
  );
};
